import { z } from "zod";
export const supportedLocales = [
    "vi",
    "zh-Hans",
    "zh-Hant",
    "en",
    "ko",
    "ru",
    "ja",
    "th",
];
export const orderStatuses = [
    "awaiting_confirmation",
    "confirmed",
    "preparing",
    "ready",
    "served",
    "completed",
    "cancelled",
];
export const itemStatuses = [
    "queued",
    "preparing",
    "ready",
    "served",
    "cancelled",
];
export const merchantRoles = [
    "owner",
    "manager",
    "cashier",
    "waiter",
    "kitchen",
    "menu_editor",
    "data_viewer",
];
export const merchantPermissions = [
    "view_dashboard",
    "manage_orders",
    "manage_queue",
    "manage_menu",
    "use_ai_translation",
    "manage_printers",
    "manage_staff",
    "manage_settings",
];
export const joinCartSchema = z.object({
    storeId: z.string().min(1),
    tableId: z.string().optional(),
    tableSessionId: z.string().optional(),
    dinerId: z.string().min(1),
    dinerName: z.string().min(1).max(30),
    locale: z.enum(supportedLocales),
    currency: z.string().length(3),
});
export const cartItemSchema = z.object({
    menuItemId: z.string().min(1),
    dinerId: z.string().min(1),
    dinerName: z.string().min(1),
    quantity: z.number().int().min(1).max(20),
    notes: z.string().max(200).default(""),
    fulfillmentMode: z.enum(["dine_in", "takeaway"]),
    modifiers: z
        .array(z.object({
        groupId: z.string(),
        optionId: z.string(),
        name: z.record(z.string(), z.string()),
        priceDelta: z.number(),
    }))
        .default([]),
});
export const presenceSchema = z.object({
    storeId: z.string(),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    accuracy: z.number().min(0).max(10_000),
    tableSessionId: z.string().optional(),
});
export const createOrderSchema = z.object({
    cartId: z.string(),
    fulfillmentMode: z.enum(["dine_in", "takeaway"]),
    serviceMode: z.enum(["table_service", "self_pickup"]),
    paymentMethod: z.enum(["cash", "merchant_qr", "pos", "online_provider"]),
    customerLocale: z.enum(supportedLocales),
    waiterLanguage: z.enum(supportedLocales).default("vi"),
    presenceToken: z.string().optional(),
    staffPresenceCode: z.string().optional(),
    entrySource: z.enum(["standard", "queue_qr"]).default("standard"),
    queueTicketId: z.string().optional(),
});
export const queueTicketSchema = z.object({
    storeId: z.string(),
    dinerId: z.string().min(1).max(100).optional(),
    customerLocale: z.enum(supportedLocales).default("en"),
    partySize: z.number().int().min(1).max(30),
    tableType: z.string().max(40).default("any"),
    source: z.enum(["onsite", "remote", "staff"]).default("onsite"),
});
export const reviewSchema = z.object({
    orderId: z.string(),
    dinerId: z.string(),
    overall: z.number().int().min(1).max(5),
    taste: z.number().int().min(1).max(5),
    service: z.number().int().min(1).max(5),
    environment: z.number().int().min(1).max(5),
    value: z.number().int().min(1).max(5),
    text: z.string().max(1000).default(""),
    locale: z.enum(supportedLocales),
    anonymous: z.boolean().default(false),
});
export const merchantLoginSchema = z.object({
    merchantCode: z.string().trim().min(2).max(40),
    username: z.string().trim().min(2).max(60),
    password: z.string().min(8).max(200),
});
export const createStaffSchema = z.object({
    username: z
        .string()
        .trim()
        .min(2)
        .max(60)
        .regex(/^[a-zA-Z0-9._-]+$/),
    displayName: z.string().trim().min(1).max(80),
    password: z.string().min(10).max(200),
    preferredLanguage: z.enum(supportedLocales),
    roles: z.array(z.enum(merchantRoles)).min(1),
    storeIds: z.array(z.string().min(1)).length(1),
});
export const updateStaffSchema = z.object({
    displayName: z.string().trim().min(1).max(80).optional(),
    preferredLanguage: z.enum(supportedLocales).optional(),
    roles: z.array(z.enum(merchantRoles)).min(1).optional(),
    storeIds: z.array(z.string().min(1)).length(1).optional(),
    active: z.boolean().optional(),
});
export const translateMenuSchema = z.object({
    sourceLocale: z.enum(supportedLocales),
    targetLocales: z
        .array(z.enum(supportedLocales))
        .min(1)
        .max(supportedLocales.length),
});
export function localize(text, locale, fallback = "en") {
    return (text[locale] ?? text[fallback] ?? text["vi"] ?? Object.values(text)[0] ?? "");
}
export function cartItemUnitPrice(menuItem, item) {
    return (menuItem.price +
        item.modifiers.reduce((sum, modifier) => sum + modifier.priceDelta, 0));
}
export function convertCurrency(amount, rates, target) {
    if (target === rates.base)
        return amount;
    const rate = rates.rates[target];
    return rate === undefined ? amount : amount * rate;
}
//# sourceMappingURL=index.js.map